// BubbleComponent.js
import React, { useEffect, useRef, useState } from "react"; // Import useState
import { motion } from "framer-motion";
import "./BubbleComponent.css"; // CSS will be simplified
import { colors } from "./constants/colors";
import { hexToRGBA } from "./utils/colorUtils";

const BUBBLE_BASE_SPEED = 1.1; // Slightly increased speed

// Define a few distinct colors for the bubbles using primary colors from App.css
const bubbleVisuals = [
  { background: "radial-gradient(circle at 30% 30%, #f870a8, #fb4296 90%)" }, // Richer Pink to Primary Pink
  { background: "radial-gradient(circle at 30% 30%, #b088f0, #7a40ca 90%)" }, // Richer Purple to Primary Purple
  { background: "radial-gradient(circle at 30% 30%, #9fa8f8, #414bb2 90%)" }, // Richer Blue to Primary Blue
  // Add more if you have more bubbles, or they will cycle through these three
];

const BubbleComponent = () => {
  const containerRef = useRef(null);
  const animationFrameIdRef = useRef(null);

  // Initial bubble definitions
  const initialBubbles = [
    {
      id: 1,
      text: "Critical Thinker",
      initialPercentagePosition: { top: "15%", left: "25%" },
      size: 110,
    },
    {
      id: 2,
      text: "Strong Communicator",
      initialPercentagePosition: { top: "50%", left: "55%" },
      size: 130,
    },
    {
      id: 3,
      text: "Creative Catalyst",
      initialPercentagePosition: { top: "30%", left: "70%" },
      size: 100,
    },
    // Add more bubbles here
  ];

  const [bubblesData, setBubblesData] = useState(() =>
    initialBubbles.map((b, index) => ({
      ...b,
      x: 0, // Pixel position within padded area, calculated in useEffect
      y: 0,
      vx: (Math.random() - 0.5) * 2 * BUBBLE_BASE_SPEED, // Random initial velocity x
      vy: (Math.random() - 0.5) * 2 * BUBBLE_BASE_SPEED, // Random initial velocity y
      ref: React.createRef(), // Ref for direct DOM manipulation
      visual: bubbleVisuals[index % bubbleVisuals.length], // Assign color/gradient
    }))
  );

  // Initialize Bubbles: Convert % to px, set initial style
  useEffect(() => {
    if (!containerRef.current) return;

    const containerEl = containerRef.current;
    const containerRect = containerEl.getBoundingClientRect();
    const computedStyle = getComputedStyle(containerEl);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingRight = parseFloat(computedStyle.paddingRight);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const effectiveWidth = containerRect.width - paddingLeft - paddingRight;
    const effectiveHeight = containerRect.height - paddingTop - paddingBottom;

    setBubblesData((prevBubbles) =>
      prevBubbles.map((bubble) => {
        let initialX =
          (parseFloat(bubble.initialPercentagePosition.left) / 100) *
          effectiveWidth;
        let initialY =
          (parseFloat(bubble.initialPercentagePosition.top) / 100) *
          effectiveHeight;

        // Clamp initial position to be within the effective boundaries, accounting for bubble size
        initialX = Math.max(
          0,
          Math.min(initialX, effectiveWidth - bubble.size)
        );
        initialY = Math.max(
          0,
          Math.min(initialY, effectiveHeight - bubble.size)
        );

        if (bubble.ref.current) {
          bubble.ref.current.style.position = "absolute";
          bubble.ref.current.style.width = `${bubble.size}px`;
          bubble.ref.current.style.height = `${bubble.size}px`;
          bubble.ref.current.style.left = `${paddingLeft + initialX}px`;
          bubble.ref.current.style.top = `${paddingTop + initialY}px`;
          // Apply the unique background from the bubble's visual property
          if (bubble.visual && bubble.visual.background) {
            bubble.ref.current.style.background = bubble.visual.background;
          }
        }
        return { ...bubble, x: initialX, y: initialY };
      })
    );
  }, []); // Run once on mount

  // Animation Loop
  useEffect(() => {
    if (
      !containerRef.current ||
      !bubblesData.every((b) => b.ref.current && b.x !== undefined)
    ) {
      return; // Wait for container, refs, and initial positions
    }

    const containerEl = containerRef.current;
    const computedStyle = getComputedStyle(containerEl);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingTop = parseFloat(computedStyle.paddingTop);

    // Get dimensions from the initial setup (assuming fixed size after mount for this example)
    // These are relative to the padded area.
    let effectiveWidth, effectiveHeight;
    if (bubblesData.length > 0 && bubblesData[0].x !== undefined) {
      // Check if init ran
      const containerRect = containerEl.getBoundingClientRect();
      const paddingRight = parseFloat(computedStyle.paddingRight);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);
      effectiveWidth = containerRect.width - paddingLeft - paddingRight;
      effectiveHeight = containerRect.height - paddingTop - paddingBottom;
    } else {
      return; // Not initialized yet
    }

    const animate = () => {
      setBubblesData((prevBubbles) =>
        prevBubbles.map((bubble) => {
          if (!bubble.ref.current) return bubble;

          let newX = bubble.x + bubble.vx;
          let newY = bubble.y + bubble.vy;
          let newVx = bubble.vx;
          let newVy = bubble.vy;

          const BOUNDARY_ADJUSTMENT = 1; // px - adjust this value if needed (e.g., 0.5, 1, or 1.5)

          const minBoundaryX = 0 - BOUNDARY_ADJUSTMENT;
          const minBoundaryY = 0 - BOUNDARY_ADJUSTMENT;
          const maxBoundaryX =
            effectiveWidth - bubble.size + BOUNDARY_ADJUSTMENT;
          const maxBoundaryY =
            effectiveHeight - bubble.size + BOUNDARY_ADJUSTMENT;

          if (newX < minBoundaryX) {
            newX = minBoundaryX;
            newVx = Math.abs(bubble.vx); // Bounce
          } else if (newX > maxBoundaryX) {
            newX = maxBoundaryX;
            newVx = -Math.abs(bubble.vx); // Bounce
          }

          if (newY < minBoundaryY) {
            newY = minBoundaryY;
            newVy = Math.abs(bubble.vy); // Bounce
          } else if (newY > maxBoundaryY) {
            newY = maxBoundaryY;
            newVy = -Math.abs(bubble.vy); // Bounce
          }

          // Directly update style for position
          bubble.ref.current.style.left = `${paddingLeft + newX}px`;
          bubble.ref.current.style.top = `${paddingTop + newY}px`;

          return { ...bubble, x: newX, y: newY, vx: newVx, vy: newVy };
        })
      );
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    animationFrameIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [bubblesData]); // Rerun if bubblesData changes (e.g. vx/vy update)

  // Framer Motion variants for hover and tap
  const bubbleInteractionVariants = {
    hover: {
      scale: 1.15,
      boxShadow: `0 0 25px ${hexToRGBA(colors.primary.purple, 0.7)}`,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
      },
    },
    tap: {
      scale: 0.9,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17,
      },
    },
  };

  return (
    <div className="container" ref={containerRef}>
      {bubblesData.map((bubble, index) => (
        <motion.div
          key={bubble.id}
          ref={bubble.ref}
          className="elem"
          style={{
            zIndex: index + 1,
          }}
          variants={bubbleInteractionVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <span className="elem-text">{bubble.text}</span>
        </motion.div>
      ))}
    </div>
  );
};

export default BubbleComponent;
